import React, { useEffect, useRef } from "react";

import "../../styles/ImageEditor_styles.css";

import { ImageEditorComponent } from "@syncfusion/ej2-react-image-editor";
import { auth, storage } from "../../helper/firebaseClient";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import useAuth from "../../stores/authStore";
import { api } from "../../utils/axios-instance";

const CACHE_NAME = "blob-cache";

const ImageEditor = ({ preUrl, fileName, fileId, setSaving, mimetype }) => {
  const imageEditorRef = useRef(null);
  const profileData = useAuth((state) => state.profileData);

  useEffect(() => {
    if (imageEditorRef.current) {
      const fetchImage = async () => {
        const response = await fetch(preUrl);

        if (!response.ok) {
          throw new Error("Failed to fetch the image file");
        }

        const blob = await response.blob();

        // Convert blob to base64
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result;
          imageEditorRef.current.open(base64data); // Assuming the editor accepts base64 encoded data
        };
        reader.readAsDataURL(blob); // Convert blob to base64 string
      };
      fetchImage();
    }
  }, [fileName, preUrl, mimetype]);

  const handleSave = () => {
    setSaving(true); // Set the saving state to true

    const imageData = imageEditorRef.current.getImageData();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.height = imageData.height;
    canvas.width = imageData.width;
    ctx.putImageData(imageData, 0, 0);

    canvas.toBlob(async (blob) => {
      const file = new File([blob], fileName, { type: mimetype });
      // Save to firebase — always the canonical object path (fileId),
      // never a version id. Keeps bytes and the storage pointer in sync.
      const fileRef = ref(storage, `files/${profileData.org_id}/${fileId}`);
      const metadata = {
        customMetadata: {
          department_id: profileData.dept,
          org_id: profileData.org_id,
        },
      };

      const uploadTask = uploadBytesResumable(fileRef, file, metadata);

      uploadTask.on(
        "state_changed",
        null,
        (error) => {
          console.error("File upload error:", error);
          // Always clear the saving flag so the UI never hangs on a failed save.
          setSaving(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          console.log("File available at:", downloadURL);

          const token = await auth.currentUser.getIdToken();
          const fileIdentifier = `${fileId}-${token}`;

          // Refresh download_url server-side. Overwriting a GCS object rotates
          // its token, so the previously stored download_url is now stale.
          try {
            await api.post(`file/update-file-version/${fileId}/`, {
              download_url: downloadURL,
              idToken: token,
            });
          } catch (err) {
            // Non-fatal — metadata update failure does not stop editing.
            console.warn(
              "update-file-version failed (non-fatal):",
              err?.response?.status,
              err?.response?.data
            );
          }

          // Bust the read cache so the next open re-fetches fresh bytes.
          const cache = await caches.open(CACHE_NAME);
          await cache.delete(fileIdentifier);

          setSaving(false);
        }
      );
    });
  };

  const toolbar = [
    "Annotate",
    "Finetune",
    "Filter",
    "Confirm",
    "Reset",
    "Save",
    "ZoomIn",
    "ZoomOut",
    "Crop",
    { text: "Save" },
  ];

  return (
    <>
      <ImageEditorComponent
        ref={(img) => {
          imageEditorRef.current = img;
        }}
        height="calc(100vh - 64px)"
        width="100vw"
        toolbarItemClicked={(args) => {
          if (args.item.text === "Save") {
            handleSave();
          }
        }}
        name={fileName}
        alt={fileName}
        enablePersistence={true}
        toolbar={toolbar}
      />
    </>
  );
};

export default ImageEditor;